import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Loader2, Send, Users } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { fmtIsk, fmtUsdApprox, fmtEurApprox, ISK_PER_USD, ISK_PER_EUR, sumIsk, hotelPriceForTotal } from "@/lib/price";
import { TripView } from "@/components/owner/TripView";

export default function GuestPage() {
  const { token, guestToken } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [pax, setPax] = useState("1");
  const [saving, setSaving] = useState(false);
  const [wish, setWish] = useState("");
  const [wishSending, setWishSending] = useState(false);
  const [daily, setDaily] = useState([]);

  const load = () =>
    axios
      .get(`${API}/trips/${token}/g/${guestToken}`)
      .then((res) => setData(res.data))
      .catch((e) => setError(e.response?.status === 403 ? "preparing" : "missing"));

  useEffect(() => {
    load();
    axios
      .get(`${API}/trips/${token}/daily`)
      .then((res) => setDaily(res.data.days || []))
      .catch(() => {});
  }, [token, guestToken]);

  const register = async () => {
    if (!name.trim() || !email.trim()) {
      toast.error("Please add your name and email.");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/trips/${token}/g/${guestToken}/register`, { name, email, pax });
      toast.success("Welcome aboard — your profile is ready");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not save your profile.");
    } finally {
      setSaving(false);
    }
  };

  const updatePax = async (g) => {
    setSaving(true);
    try {
      await axios.post(`${API}/trips/${token}/g/${guestToken}/register`, { name: g.name, email: g.email, pax });
      toast.success("Party size updated");
      load();
    } catch {
      toast.error("Could not update.");
    } finally {
      setSaving(false);
    }
  };

  const inputCls =
    "w-full border border-amber-900/40 bg-[#0d0d12] px-4 py-3 text-sm text-amber-100 placeholder:text-neutral-600 outline-none transition-colors focus:border-amber-400";

  const guest = data?.guest;
  const trip = data?.trip;

  const totalIsk = trip ? sumIsk([...(trip.hotels || []).map(hotelPriceForTotal), ...(trip.items || []).map((x) => x.price)]) : 0;

  return (
    <div data-testid="guest-page" className="min-h-screen bg-[#070709] text-neutral-100">
      <div className="mx-auto max-w-3xl px-5 py-14 sm:py-20">
        <div className="flex flex-col items-center text-center">
          <img src="/assets/logo.png" alt="Exclusive Reykjavik" className="h-16 w-auto" />
        </div>

        {error === "missing" && (
          <div data-testid="guest-not-found" className="mt-14 border border-dashed border-neutral-700 py-20 text-center">
            <p className="font-serif text-3xl font-light text-neutral-300">This invitation has wandered off the map.</p>
            <p className="mt-3 text-sm text-neutral-500">The link may be incomplete — ask your concierge to resend it.</p>
          </div>
        )}
        {error === "preparing" && (
          <div className="mt-14 border border-dashed border-amber-500/30 py-20 text-center">
            <p className="font-serif text-3xl font-light text-neutral-200">Still being composed.</p>
            <p className="mt-3 text-sm text-neutral-500">Your concierge is putting the final touches on this journey.</p>
          </div>
        )}
        {!error && !data && (
          <div className="mt-20 flex justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-amber-400" />
          </div>
        )}

        {data && !guest.registered && (
          <motion.div
            data-testid="guest-register"
            className="mt-12 border border-amber-500/25 bg-[#0c0c10] p-6 sm:p-10"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
          >
            <p className="text-center text-[10px] font-semibold uppercase tracking-[0.35em] text-amber-300/80">
              You have been invited
            </p>
            <h1 className="mt-4 text-center font-luxe text-2xl uppercase leading-snug tracking-[0.15em] text-neutral-50 sm:text-3xl">
              {trip.client.name}&apos;s journey
            </h1>
            <p className="mx-auto mt-4 max-w-md text-center text-sm leading-relaxed text-neutral-400">
              Create your profile to see the full itinerary and costs — and tell us how many you are bringing.
            </p>
            <div className="mt-8 space-y-4">
              <input data-testid="guest-name-input" placeholder="Your full name *" value={name} onChange={(e) => setName(e.target.value)} className={inputCls} />
              <input data-testid="guest-email-input" type="email" placeholder="Your email *" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} />
              <div className="block">
                <span className="text-[10px] font-semibold uppercase tracking-[0.3em] text-neutral-500">
                  Are you travelling solo or as two? (including you)
                </span>
                <div className="mt-2 grid grid-cols-2 gap-2">
                  {[["1", "Just me"], ["2", "Two of us"]].map(([n, label]) => (
                    <button
                      key={n}
                      type="button"
                      data-testid={`guest-pax-${n}`}
                      onClick={() => setPax(n)}
                      aria-pressed={pax === n}
                      className={`border px-3 py-2.5 text-xs font-semibold uppercase tracking-[0.15em] transition-colors ${
                        pax === n
                          ? "border-amber-400/70 bg-amber-500/15 text-amber-200"
                          : "border-neutral-700 text-neutral-400 hover:border-amber-500/40 hover:text-amber-100"
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
                {pax === "1" && (
                  <p data-testid="guest-pax-solo-note" className="mt-2 text-[10px] leading-relaxed text-neutral-500">
                    Travelling solo — dinners are billed at half price for one.
                  </p>
                )}
              </div>
              <button
                data-testid="guest-join-button"
                onClick={register}
                disabled={saving}
                className="flex w-full items-center justify-center gap-2 bg-amber-500 px-8 py-4 text-xs font-semibold uppercase tracking-[0.25em] text-black transition-colors hover:bg-amber-300 disabled:opacity-60"
              >
                {saving && <Loader2 className="h-4 w-4 animate-spin" />}
                Join the Journey
              </button>
            </div>
          </motion.div>
        )}

        {data && guest.registered && (
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <div data-testid="guest-profile" className="mt-10 border border-amber-500/25 bg-amber-500/5 p-6 text-center">
              <p className="text-[10px] font-semibold uppercase tracking-[0.35em] text-amber-300/80">Your profile</p>
              <h1 className="mt-2 font-luxe text-2xl uppercase tracking-[0.15em] text-neutral-50">{guest.name}</h1>
              <div className="mt-3 flex flex-wrap items-center justify-center gap-3 text-[11px] uppercase tracking-[0.2em] text-neutral-400">
                <span className="border border-amber-500/25 px-3 py-1.5">{guest.email}</span>
                <span className="flex items-center gap-2 border border-amber-500/25 px-3 py-1.5">
                  <Users className="h-3.5 w-3.5 text-amber-400/70" />
                  Party of {guest.pax}
                </span>
              </div>
              <div className="mt-4 flex items-center justify-center gap-2">
                <span className="text-[10px] uppercase tracking-[0.2em] text-neutral-500">Change party size:</span>
                <input
                  data-testid="guest-pax-edit"
                  type="number"
                  min="1"
                  max="100"
                  value={pax || guest.pax}
                  onChange={(e) => setPax(e.target.value)}
                  className="w-20 border border-amber-900/40 bg-[#0d0d12] px-3 py-1.5 text-sm text-amber-100 outline-none focus:border-amber-400"
                />
                <button
                  data-testid="guest-pax-save"
                  onClick={() => updatePax(guest)}
                  disabled={saving}
                  className="border border-amber-500/50 px-4 py-1.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-amber-200 transition-colors hover:bg-amber-500 hover:text-black disabled:opacity-50"
                >
                  Save
                </button>
              </div>
            </div>

            <div className="mt-10 border border-amber-500/20 bg-[#0c0c10]/80 p-6 sm:p-10">
              <TripView trip={data.trip} daily={daily} />
            </div>

            {totalIsk > 0 && (
              <div data-testid="guest-total" className="mt-6 border border-amber-500/30 bg-amber-500/5 p-5 text-center">
                <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-amber-300/80">Indicative total</p>
                <p className="mt-2 font-luxe text-3xl tracking-wide text-amber-100">
                  {fmtIsk(totalIsk)} <span className="text-base font-normal normal-case tracking-normal text-neutral-500">{trip.client_region === "eu" ? fmtEurApprox(totalIsk / ISK_PER_EUR) : fmtUsdApprox(totalIsk / ISK_PER_USD)}</span>
                </p>
                <p className="mt-2 text-[10px] uppercase tracking-[0.2em] text-neutral-600">
                  Final quote confirmed by your concierge
                </p>
              </div>
            )}

            <div data-testid="guest-wish-box" className="mt-6 border border-amber-500/20 bg-[#0c0c10]/80 p-5 sm:p-6">
              <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-neutral-500">
                Request a change
              </p>
              <textarea
                data-testid="guest-wish-input"
                rows={2}
                placeholder="Anything you'd like different — 'can we swap the ATV tour for snowmobiles?' — the concierge turns it into changes"
                value={wish}
                onChange={(e) => setWish(e.target.value)}
                className="mt-3 w-full resize-none border border-amber-900/40 bg-[#0d0d12] px-4 py-3 text-sm text-amber-100 placeholder:text-neutral-600 outline-none transition-colors focus:border-amber-400"
              />
              <button
                data-testid="guest-wish-send"
                onClick={async () => {
                  if (!wish.trim()) {
                    toast.error("Write your wish first.");
                    return;
                  }
                  setWishSending(true);
                  try {
                    await axios.post(`${API}/trips/${token}/g/${guestToken}/wish`, { text: wish });
                    setWish("");
                    toast.success("Sent to your concierge");
                    load();
                  } catch (e) {
                    toast.error(e.response?.data?.detail || "Could not send right now.");
                  } finally {
                    setWishSending(false);
                  }
                }}
                disabled={wishSending}
                className="mt-3 flex items-center gap-2 border border-amber-500/50 px-6 py-2.5 text-[10px] font-semibold uppercase tracking-[0.25em] text-amber-200 transition-colors hover:bg-amber-500 hover:text-black disabled:opacity-50"
              >
                {wishSending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                Send to Concierge
              </button>

              {data.wishes?.length > 0 && (
                <div className="mt-4 space-y-2 border-t border-amber-500/10 pt-4">
                  {data.wishes.map((w, i) => (
                    <div key={i} data-testid={`guest-wish-${i}`} className="border border-neutral-800 p-3 text-left">
                      <p className="text-sm italic text-neutral-300">&ldquo;{w.text}&rdquo;</p>
                      {w.reply && (
                        <p data-testid={`guest-wish-reply-${i}`} className="mt-2 border-l-2 border-amber-500/60 pl-3 text-sm text-amber-100/90">
                          <span className="text-[10px] font-semibold uppercase tracking-[0.25em] text-amber-300/80">Your concierge</span>
                          <br />
                          {w.reply}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        )}

        <div className="mt-12 text-center">
          <Link to="/" className="text-[10px] uppercase tracking-[0.3em] text-neutral-600 transition-colors hover:text-amber-200">
            Exclusive Reykjavik · Key to the city — and beyond
          </Link>
        </div>
      </div>
    </div>
  );
}
